import type { z } from 'zod';

import { API_BASE } from './base';
import { APP_VERSION, APP_VERSION_HEADER } from '../native/version';

import { PROBLEM, ProblemError, toProblem } from './problem';

/**
 * The typed fetcher every screen goes through (BP-01 §6 "Shared `lib/`:
 * `api/` — generated + `fetcher` with CSRF and bearer").
 *
 * Two authentication modes, one transport:
 *
 * * **cookie** (the staff and client web apps, BP-01 R5) — `credentials:
 *   'include'` so the HttpOnly `sid` cookie rides along, and the readable `csrf`
 *   cookie is echoed in the `X-CSRF` header on every unsafe method. The header
 *   name and the cookie name are `api/app/core/security/sessions.py`'s, verbatim.
 * * **bearer** (the Capacitor app, BP-01 R6) — `Authorization: Bearer <access>`
 *   plus a refresh interceptor: a 401 triggers one rotation of the opaque
 *   refresh token and one retry. The rotation is **single-flight**, because R6
 *   makes a refresh token single-use and "reuse of a consumed refresh revokes
 *   the whole chain" — two concurrent 401s that each rotated would log the user
 *   out. A refresh that fails clears the stored pair and calls `onSessionLost`.
 *
 * Errors are RFC 9457 problem documents (`./problem`), so a screen branches on
 * `urn:26ad:tenant-readonly` rather than on 423.
 */

/** The subset of `fetch` this module uses; the tests substitute their own. */
export type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

/** Methods RFC 9110 calls safe — no CSRF token is required for them (R5). */
export const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

/** The readable companion cookie of the double-submit CSRF check (R5). */
export const CSRF_COOKIE = 'csrf';

/** The header the client echoes the `csrf` cookie in (R5, verbatim). */
export const CSRF_HEADER = 'X-CSRF';

/** The status that starts the bearer-mode refresh dance. */
export const UNAUTHORIZED = 401;

/** One request, as the endpoint helpers describe it. */
export interface RequestSpec {
  method: string;
  /**
   * The request body.
   *
   * JSON-serialised when present, **except** a `FormData`, which is sent
   * as it is so `fetch` can set the multipart boundary itself.
   */
  body?: unknown;
  /** Extra headers, e.g. ARCHITECTURE §3 API's `Idempotency-Key` on a POST. */
  headers?: Record<string, string>;
}

/** What an authentication mode contributes to every request. */
export interface AuthAdapter {
  /** `fetch`'s `credentials` for this mode. */
  readonly credentials: RequestCredentials;
  /** Headers this request must carry; may be empty. */
  headers: (method: string) => Record<string, string>;
  /** Try to recover from a 401. Resolves `true` when the request should be retried. */
  recover: () => Promise<boolean>;
}

/** The access/refresh pair `POST /auth/token` and `/auth/refresh` return (R6). */
export interface TokenPair {
  access: string;
  refresh: string;
}

/** Where the app keeps its token pair (memory, `localStorage`, Capacitor storage). */
export interface TokenStore {
  read: () => TokenPair | null;
  write: (pair: TokenPair) => void;
  clear: () => void;
}

/** The typed transport the endpoint helpers are built on. */
export interface Fetcher {
  /** Send a request and parse its body with `schema`. */
  json: <T>(path: string, spec: RequestSpec, schema: z.ZodType<T>) => Promise<T>;
  /** Send a request whose success has no body (a 204). */
  empty: (path: string, spec: RequestSpec) => Promise<void>;
}

/** Read one cookie out of a `document.cookie` string, or `null`. */
export function readCookie(name: string, jar: string): string | null {
  const prefix = `${name}=`;
  const hit = jar.split('; ').find((entry) => entry.startsWith(prefix));
  if (hit === undefined) {
    return null;
  }
  return decodeURIComponent(hit.slice(prefix.length));
}

/**
 * Cookie mode (R5): nothing on safe methods, `X-CSRF` on everything else, and a
 * 401 is final — there is no refresh for a browser session, the user logs in again.
 */
export function createCookieAuth(readJar: () => string): AuthAdapter {
  return {
    credentials: 'include',
    headers: (method) => {
      if (SAFE_METHODS.has(method)) {
        return {};
      }
      const csrf = readCookie(CSRF_COOKIE, readJar());
      if (csrf === null) {
        return {};
      }
      return { [CSRF_HEADER]: csrf };
    },
    recover: () => Promise.resolve(false),
  };
}

/**
 * Bearer mode (R6) with a single-flight refresh interceptor.
 *
 * `rotate` performs `POST /auth/refresh` and resolves the new pair, or `null`
 * when the api refused it. Concurrent 401s share one in-flight rotation: the
 * first caller starts it, every other caller awaits the same promise, and all of
 * them retry once it resolves.
 */
export function createBearerAuth(
  tokens: TokenStore,
  rotate: (refresh: string) => Promise<TokenPair | null>,
  onSessionLost: () => void,
): AuthAdapter {
  let inflight: Promise<boolean> | null = null;

  async function runRotation(): Promise<boolean> {
    const held = tokens.read();
    if (held === null) {
      onSessionLost();
      return false;
    }
    const next = await rotate(held.refresh);
    if (next === null) {
      tokens.clear();
      onSessionLost();
      return false;
    }
    tokens.write(next);
    return true;
  }

  return {
    credentials: 'same-origin',
    headers: () => {
      const held = tokens.read();
      if (held === null) {
        return {};
      }
      return { authorization: `Bearer ${held.access}` };
    },
    recover: () => {
      inflight ??= runRotation().finally(() => {
        inflight = null;
      });
      return inflight;
    },
  };
}

/** Build a fetcher over `auth`. `baseUrl` is prefixed to every path. */
export function createFetcher(auth: AuthAdapter, baseUrl: string, fetchImpl: FetchLike): Fetcher {
  function send(path: string, spec: RequestSpec): Promise<Response> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      // BP-11 R3: every request carries the build it came from, so the api
      // can turn away a build below `mobile.min_supported_version` with 426
      // instead of letting it fail one screen at a time. The PWA sends it
      // too — it is the same bundle, and a floor that applied only to the
      // store build would be a floor the shop could not test.
      [APP_VERSION_HEADER]: APP_VERSION,
      ...spec.headers,
      ...auth.headers(spec.method),
    };
    const init: RequestInit = {
      method: spec.method,
      headers,
      credentials: auth.credentials,
    };
    if (spec.body instanceof FormData) {
      // Passed through untouched, and **without** a content-type: a
      // multipart body needs a boundary, and only `fetch` knows the one
      // it generated. Setting `multipart/form-data` by hand here would
      // send a body the server cannot split — BP-16's settlement upload
      // is the first thing in this app to need it.
      init.body = spec.body;
    } else if (spec.body !== undefined) {
      headers['content-type'] = 'application/json';
      init.body = JSON.stringify(spec.body);
    }
    return fetchImpl(baseUrl + path, init);
  }

  async function execute(path: string, spec: RequestSpec): Promise<Response> {
    const first = await send(path, spec);
    if (first.status !== UNAUTHORIZED) {
      return first;
    }
    const recovered = await auth.recover();
    if (!recovered) {
      return first;
    }
    return send(path, spec);
  }

  async function failure(response: Response): Promise<ProblemError> {
    const body: unknown = await response.json().catch(() => null);
    return new ProblemError(toProblem(response.status, body));
  }

  return {
    json: async <T>(path: string, spec: RequestSpec, schema: z.ZodType<T>): Promise<T> => {
      const response = await execute(path, spec);
      if (!response.ok) {
        throw await failure(response);
      }
      const parsed = schema.safeParse(await response.json());
      if (!parsed.success) {
        throw new ProblemError({
          type: PROBLEM.contractMismatch,
          title: 'Unexpected response',
          status: response.status,
          detail: `${spec.method} ${path} returned a body that does not match the OpenAPI contract`,
        });
      }
      return parsed.data;
    },
    empty: async (path: string, spec: RequestSpec): Promise<void> => {
      const response = await execute(path, spec);
      if (!response.ok) {
        throw await failure(response);
      }
    },
  };
}

/** The browser's cookie jar — the one impure read cookie mode needs. */
export function documentCookieJar(): string {
  return document.cookie;
}

/** The fetcher the two web apps use: cookie session, `X-CSRF`, real `fetch`. */
export function createWebFetcher(): Fetcher {
  return createFetcher(createCookieAuth(documentCookieJar), API_BASE, (input, init) =>
    fetch(input, init),
  );
}
